import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { quotesAPI, categoriesAPI } from '../services/api';
import { getChecklistForService } from '../data/serviceChecklists';
import { FileText, MapPin } from 'lucide-react';

const RequestQuotePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(location.state?.category || '');
  const [selectedChecklist, setSelectedChecklist] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    location: user?.location || '',
    service: '',
    description: '',
    preferredDate: '',
    packageId: location.state?.packageId || null
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoriesAPI.getAll();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        location: user.location || prev.location
      }));
    }
  }, [user]);

  const checklist = selectedCategory ? getChecklistForService(selectedCategory) : null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleChecklistItem = (item) => {
    setSelectedChecklist(prev => 
      prev.includes(item) 
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Format the service description
      let serviceDescription = `Main Service: ${formData.service}`;
      
      // Add additional services if selected
      if (selectedChecklist.length > 0) {
        serviceDescription += '\n\nAdditional Services:\n- ' + selectedChecklist.join('\n- ');
      }
      
      // Add user description (if provided)
      const fullDescription = formData.description 
        ? serviceDescription + '\n\nDetails:\n' + formData.description
        : serviceDescription;
      
      await quotesAPI.create({
        ...formData,
        providerId: 'general', // General quote request, not for specific provider
        description: fullDescription
      });

      toast({
        title: 'Quote Request Submitted!',
        description: 'Service providers in your area will contact you soon.',
      });

      // Reset form
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        location: user?.location || '',
        service: '',
        description: '',
        preferredDate: ''
      });
      setSelectedChecklist([]);
      setSelectedCategory('');

      // Redirect to my quotes page
      setTimeout(() => {
        navigate('/my-quotes');
      }, 2000);
    } catch (error) {
      // Check if it's a quota limit error
      if (error.response?.status === 403) {
        toast({
          title: 'Monthly Limit Reached',
          description: error.response?.data?.detail || 'You have reached your monthly quote limit. Upgrade your plan to continue.',
          variant: 'destructive',
          duration: 5000
        });
        
        // Show upgrade button after a brief delay
        setTimeout(() => {
          if (window.confirm('Would you like to upgrade your plan to get unlimited quotes?')) {
            navigate('/pricing');
          }
        }, 1000);
      } else {
        toast({
          title: 'Request Failed',
          description: 'Could not submit quote request. Please try again.',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Request a Quote</h1>
          <p className="text-gray-600 mt-2">Tell us about your project and get matched with qualified professionals</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-orange-600" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Category Selection */}
              <div>
                <Label htmlFor="category">Service Category *</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a service category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="location"
                      name="location"
                      placeholder="City, Province or Postal Code"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="service">Specific Service Needed *</Label>
                <Select 
                  value={formData.service} 
                  onValueChange={(value) => setFormData({ ...formData, service: value })}
                  required
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select the specific service you need" />
                  </SelectTrigger>
                  <SelectContent>
                    {checklist && checklist.options.map((option, index) => (
                      <SelectItem key={index} value={option}>{option}</SelectItem>
                    ))}
                    {!checklist && <SelectItem value="general">General Service</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Select the main service type from the list above</p>
              </div>

              {/* Additional Services Checklist */}
              {checklist && formData.service && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <Label className="text-base font-semibold mb-3 block">Additional Services (Optional)</Label>
                  <p className="text-sm text-gray-600 mb-3">Select any additional services you need:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {checklist.options
                      .filter(option => option !== formData.service)
                      .map((option, index) => (
                        <label
                          key={index}
                          className="flex items-start space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedChecklist.includes(option)}
                            onChange={() => toggleChecklistItem(option)}
                            className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="description">Additional Details (Optional)</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Please provide detailed information about your project, timeline, budget, and any specific requirements..."
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 min-h-[120px]"
                />
              </div>

              <div>
                <Label htmlFor="preferredDate">Preferred Start Date</Label>
                <Input
                  id="preferredDate"
                  name="preferredDate"
                  type="date"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>What happens next?</strong> Multiple service providers will receive your request and contact you with quotes. You can then compare and choose the best option.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default RequestQuotePage;